import { Context } from '~/context/auth';
import React, { useContext } from 'react';
import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  useLocation,
} from 'react-router-dom';

import Layout from './components/Layout';
import ScreenLoader from './components/ScreenLoader';
import { FilterProvider } from './context/filter';
import { MacroProvider } from './context/macros';
import { NavigationProvider } from './context/navigation';
import Login from './pages/Login';
import PdvOnline from './pages/PdvOnline';
import Recovery from './pages/Recovery';
import { LoadingProvider } from './context/loading';
import { TableViewManagerProvider } from './context/tableViewManager';

type ApplicationProps = {
  url?: string;
};

const Application: React.FC<ApplicationProps> = ({ url }) => (
  <NavigationProvider>
    <MacroProvider>
      <FilterProvider>
        <LoadingProvider>
          <TableViewManagerProvider>
            <Layout url={url} />
          </TableViewManagerProvider>
        </LoadingProvider>
      </FilterProvider>
    </MacroProvider>
  </NavigationProvider>
);

type CustomRouteProps = RouteProps & {
  isPrivate?: boolean;
};

const CustomRoute: React.FC<CustomRouteProps> = ({
  isPrivate = false,
  ...rest
}) => {
  const { loading, authenticated, isExpired } = useContext(Context);
  const location = useLocation();

  if (loading) return <ScreenLoader />;
  if (isPrivate && !authenticated) return <Redirect to="/" />;

  if (
    isPrivate &&
    authenticated &&
    isExpired &&
    !location.pathname.startsWith('/meu-plano')
  ) {
    return <Redirect to="/meu-plano" />;
  }

  return <Route {...rest} />;
};

const getUrlFromPathname = (pathname: string): string => {
  const slug = pathname.replace(/^\//, '');
  return slug || 'app';
};

/**
 * Mantém o Layout e Providers "vivos" enquanto a rota muda,
 * preservando o estado das abas abertas.
 */
const ApplicationShell: React.FC = () => {
  const { pathname } = useLocation();
  return <Application url={getUrlFromPathname(pathname)} />;
};

const PRIVATE_APP_ROUTE_PATHS: Array<string> = [
  '/app',
  '/controle-de-acesso',
  '/usuario',
  '/loja',
  '/teste',
  '/pdv',
  '/tributacao',
  '/operador-de-caixa',
  '/serie-de-nf',
  '/cest',
  '/componente',
  '/ncm',
  '/departamento',
  '/cliente',
  '/tipo-de-ajuste-de-estoque',
  '/grupo-de-empresa',
  '/fornecedor',
  '/regras-de-loja',
  '/regras-de-empresa',
  '/tabela-de-precos',
  '/produto',
  '/similares',
  '/ajuste-de-estoque',
  '/check-list',
  '/mix-de-produto',
  '/tara',
  '/categoria-financeira',
  '/informacao-nutricional',
  '/receita',
  '/alteracao-de-preco',
  '/finalizadora',
  '/conta-bancaria',
  '/certificado-digital',
  '/banco',
  '/carga',
  '/programacao-de-oferta',
  '/contas-a-receber',
  '/emissao-nfe',
  '/feriado',
  '/configuracao-de-e-mail',
  '/contas-a-pagar',
  '/aceite-de-pagamento',
  '/vincular-email',
  '/cidade',
  '/quitacao-em-lote',
  '/perfil-de-documentos',
  '/validador-de-xml',
  '/teste-de-impressao',
  '/entrada-de-nfe',
  '/monitor-de-recepcao-de-xmls',
  '/status-pdv',
  '/testando',
  '/retencoes-tributarias',
  '/relatorio-de-logs-de-nfes-de-entrada',
  '/relatorio-de-logs-de-nfes-de-saida',
  '/catalogo-ncm',
  '/classificacao-parceiro',
  '/tipo-de-dados',
  '/tipo-item',
  '/condicao',
  '/modulo',
  '/grupo-de-aplicacao',
  '/interface',
  '/modelo-etiqueta',
  '/processamento-manual-xml',
  '/central-de-liberacao-de-tokens',
  '/tipo-informacao',
  '/aplicacao',
  '/cfop',
  '/pais',
  '/ajuste-margem-produto',
  '/relatorio-de-logs-de-cargas',
  '/pauta',
  '/central-notificacao',
  '/devolucao-e-saida-nf-forn',
  '/central-de-liberação-de-terminais',
  '/relatorio-vendas',
  '/lancamento-nf-de-outras-operacoes',
  '/grupo-etiqueta',
  '/transportadora',
  '/cadastro-de-decomposicao',
  '/cancelamento-nf',
  '/alteracao-precos-entrada-fornecedor',
  '/cadastro-de-tela',
  '/edicao-conta',
  '/manutencao-fiscal',
  '/alteracao-custo-entrada-nf-bonificada',
  '/lancamento-caixa',
  '/tipo-movimento',
  '/conferencia-tesouraria',
  '/inutilizacao-nfe',
  '/liberacao-tesouraria',
  '/marcas',
  '/monitor-de-api-de-integracao',
  '/exportacao-escrita-fiscal',
  '/sped-fiscal',
  '/gia',
  '/unidade',
  '/sped-efd-contribuicoes',
  '/conferencia-venda-sat-nfce',
  '/alteracao-de-preco-por-item',
  '/relatorio-auditoria-usuarios',
  '/informacao-complementar',
  '/fechamento-fiscal',
  '/reprocessa-pis-cofins',
  '/apuracao-pis-cofins',
  '/relatorio-vendas-por-dia',
  '/registro-inventario',
  '/impressao-etiqueta',
  '/apuracao-icms',
  '/relatorio-diferenca-lancamentos',
  '/identificacao-contabil',
  '/trilha',
  '/pedido-venda',
  '/plano-contratacao',
  '/grade-de-produto',
  '/meu-plano',
  '/relatorio-raiox-vendas',
  '/manutencao-fiscal-nfce',
  '/composicao',
  '/producao',
  '/agendamento-de-contas',
  '/fluxo-de-caixa',
  '/administradora-cartoes',
  '/classificacao-ibs-cbs',
  '/movimento-producao',
  '/analise-habito-consumo',
  '/alteracao-ibs-cbs-em-lote',
  '/transferencia-pos',
  '/ajuste-de-para',
  '/relatorio-conferencia-entrada',
  '/romaneio-fornecedor',
  '/alteracao-ncm-em-lote',
];

const PUBLIC_APP_ROUTE_PATHS: Array<string> = [''];

const Routes: React.FC = () => (
  <Switch>
    {/* públicas */}
    <CustomRoute path="/" component={Login} exact />
    <CustomRoute path="/recovery/:hash" component={Recovery} exact />

    {/* privadas específicas */}
    <CustomRoute path="/pdv-online" component={PdvOnline} exact isPrivate />

    {/* Application (públicas) */}
    <CustomRoute
      path={PUBLIC_APP_ROUTE_PATHS}
      exact
      render={() => <ApplicationShell />}
    />

    {/* Application (privadas) */}
    <CustomRoute
      path={PRIVATE_APP_ROUTE_PATHS}
      exact
      render={() => <ApplicationShell />}
      isPrivate
    />

    <Redirect to="/app" />
  </Switch>
);

export default Routes;
